import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDownIcon, BellIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useExternalAuth } from '../../services/externalAuthContext';
import { useMyExternalProjects } from '../../hooks/useMyExternalProjects';

/**
 * Phase 57 Plan 57-01 — PORTAL-01
 *
 * Customer-portal top navigation bar. Replaces the left-sidebar ExternalNavigation
 * for /external/portal/* routes.
 *
 * Auth isolation (Landmine #8): ONLY useExternalAuth from services/externalAuthContext.
 * Never import the internal auth hook here — external surface must stay isolated.
 */
export function PortalTopNav() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();
  const { user, logout } = useExternalAuth();
  const projectsQuery = useMyExternalProjects();

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    }
    if (switcherOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [switcherOpen]);

  const projects = projectsQuery.data ?? [];
  const selectedProject = projects.find((p) => String(p.id) === projectId) ?? projects[0];
  const hasManyProjects = projects.length > 1;

  const handleSignOut = async () => {
    await logout();
    navigate('/external/login', { replace: true });
  };

  const handleSelectProject = (id: number) => {
    setSwitcherOpen(false);
    navigate(`/external/portal/projects/${id}/requests`);
  };

  // User initials for avatar
  const initials =
    user
      ? `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase()
      : '?';
  const displayName = user ? `${user.firstName} ${user.lastName}` : '';
  const displayEmail = user?.email ?? '';

  return (
    <header className="sticky top-0 z-30 h-14 bg-white border-b border-ms-border flex items-center justify-between px-4 gap-4">
      {/* ── Left: Brand block ── */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="w-6 h-6 bg-ms-text-strong rounded-sm flex items-center justify-center text-white text-xs font-bold">
          M
        </span>
        <span className="text-sm font-medium text-ms-text">Monksheets · Customer portal</span>
      </div>

      {/* ── Center-left: Project switcher ── */}
      <div className="relative flex-shrink-0" ref={switcherRef}>
        {projectsQuery.isLoading ? (
          /* Shimmer on switcher while me/projects resolves */
          <div className="animate-pulse h-7 w-36 rounded bg-ms-surface-2" />
        ) : projectsQuery.isError || projects.length === 0 ? (
          <span className="text-sm text-ms-text-muted">No projects</span>
        ) : (
          <button
            type="button"
            className="flex items-center gap-2 px-2 py-1 rounded hover:bg-ms-surface-2 transition-colors"
            onClick={() => hasManyProjects && setSwitcherOpen((o) => !o)}
            aria-haspopup={hasManyProjects ? 'listbox' : undefined}
            aria-expanded={hasManyProjects ? switcherOpen : undefined}
          >
            {/* Colored dot — use a simple orange dot consistent with portal accent */}
            <span className="w-2 h-2 rounded-full bg-ms-orange flex-shrink-0" />
            <span className="text-sm font-medium text-ms-text-strong max-w-[160px] truncate">
              {selectedProject?.name ?? 'Select project'}
            </span>
            {hasManyProjects && (
              <ChevronDownIcon
                className={`w-4 h-4 text-ms-text-secondary transition-transform ${switcherOpen ? 'rotate-180' : ''}`}
              />
            )}
          </button>
        )}

        {/* Dropdown */}
        {switcherOpen && hasManyProjects && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-ms-border rounded-lg shadow-lg z-50 py-1">
            {projects.map((p) => (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={String(p.id) === projectId}
                className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-ms-surface-2 transition-colors ${
                  String(p.id) === projectId
                    ? 'bg-ms-surface-2 text-ms-text-strong font-medium'
                    : 'text-ms-text'
                }`}
                onClick={() => handleSelectProject(p.id)}
              >
                <span className="w-2 h-2 rounded-full bg-ms-orange flex-shrink-0" />
                <span className="truncate">{p.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Center: Nav tabs ── */}
      <nav className="hidden md:flex items-center gap-6 flex-shrink-0">
        {/* Dashboard — disabled placeholder */}
        <button
          type="button"
          className="pointer-events-none opacity-50 cursor-not-allowed text-sm font-medium text-ms-text-secondary pb-1"
          title="Coming soon"
          tabIndex={-1}
        >
          Dashboard
        </button>

        {/* Requests — active tab */}
        <button
          type="button"
          className="text-sm font-semibold text-ms-text-strong border-b-2 border-ms-text-strong underline-offset-4 pb-1"
        >
          Requests
        </button>

        {/* Documents — disabled placeholder */}
        <button
          type="button"
          className="pointer-events-none opacity-50 cursor-not-allowed text-sm font-medium text-ms-text-secondary pb-1"
          title="Coming soon"
          tabIndex={-1}
        >
          Documents
        </button>

        {/* People — disabled placeholder */}
        <button
          type="button"
          className="pointer-events-none opacity-50 cursor-not-allowed text-sm font-medium text-ms-text-secondary pb-1"
          title="Coming soon"
          tabIndex={-1}
        >
          People
        </button>
      </nav>

      {/* ── Right cluster: Search + Bell + User block ── */}
      <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
        {/* Search — non-functional in v1 */}
        <div className="relative hidden lg:block">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-ms-text-muted opacity-50 pointer-events-none" />
          {/* TODO Phase 57+: wire search */}
          <input
            type="text"
            placeholder="Search requests"
            className="w-48 pl-8 pr-3 py-1.5 text-sm border border-ms-border rounded-lg bg-ms-canvas text-ms-text placeholder:text-ms-text-muted opacity-50 pointer-events-none focus:outline-none"
            readOnly
            tabIndex={-1}
          />
        </div>

        {/* Bell — disabled in v1 */}
        <button
          type="button"
          className="p-1.5 rounded-lg opacity-50 pointer-events-none"
          aria-label="Notifications"
          tabIndex={-1}
        >
          <BellIcon className="w-5 h-5 text-ms-text-secondary" />
        </button>

        {/* User block */}
        <div className="flex items-center gap-2 group relative">
          {/* Avatar circle with initials */}
          <span className="w-8 h-8 rounded-full bg-ms-surface-2 border border-ms-border flex items-center justify-center text-xs font-semibold text-ms-text-strong flex-shrink-0">
            {initials}
          </span>
          <div className="hidden lg:flex flex-col leading-tight">
            <span className="text-sm font-medium text-ms-text-strong truncate max-w-[120px]">
              {displayName}
            </span>
            <span className="text-xs text-ms-text-muted truncate max-w-[120px]">{displayEmail}</span>
          </div>
          {/* Sign-out affordance */}
          <button
            type="button"
            onClick={handleSignOut}
            className="ml-1 text-xs text-ms-text-secondary hover:text-ms-text transition-colors hidden lg:block"
            title="Sign out"
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
